import { Capacitor } from '@capacitor/core';
import { isPWAInstalled } from './pwaUtils';

export const isDocumentReloadOrRestore = (): boolean => {
  if (typeof performance === 'undefined') return false;
  const nav = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (nav) {
    return nav.type === 'reload' || nav.type === 'back_forward';
  }
  const legacy = (performance as unknown as { navigation?: { type?: number } }).navigation;
  const type = legacy?.type;
  return type === 1 || type === 2;
};

/** iPhone / iPad (including iPadOS desktop UA with touch). */
export const isAppleMobileDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ may report as Macintosh
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
};

export type LaunchSplashKind = 'none' | 'video' | 'image';

/**
 * Launch splash policy:
 * - video: Capacitor, Electron, Android (and other non-Apple) installed PWA
 * - image: iPhone / iPad installed PWA (MP4 autoplay is unreliable there)
 * - none: normal browser tabs, or reload / back-forward
 */
export const getLaunchSplashKind = (): LaunchSplashKind => {
  if (typeof window === 'undefined') return 'none';
  if (isDocumentReloadOrRestore()) return 'none';
  if (Capacitor.isNativePlatform() || isElectron()) return 'video';
  if (!isPWAInstalled()) return 'none';
  if (isAppleMobileDevice()) return 'image';
  return 'video';
};

/** True when the MP4 launch splash should play. */
export const shouldShowLaunchVideoSplash = (): boolean => {
  return getLaunchSplashKind() === 'video';
};

export const isElectron = (): boolean => {
  return typeof window !== 'undefined' && window.electronAPI?.isElectron === true;
};

//android and iOS 
export const isCapacitorNative = (): boolean => {
  return typeof window !== 'undefined' && Capacitor.isNativePlatform();
};

export const isAndroidApp = (): boolean => {
  return Capacitor.getPlatform() === 'android' && Capacitor.isNativePlatform();
};

export const isIosApp = (): boolean => {
  return Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform();
};

const isNativeEscPosApp = (): boolean => {
  return (isAndroidApp() || isIosApp()) && Capacitor.isPluginAvailable('EscPosPrinter');
};

export const supportsGenericEscposPrinting = (): boolean => {
  const electronReady = isElectron() && typeof window.electronAPI?.printEscposTcp === 'function';
  return electronReady || isNativeEscPosApp();
};

export const supportsUsbAndroidPrinting = (): boolean => {
  return isAndroidApp() && Capacitor.isPluginAvailable('EscPosPrinter');
};

export const supportsBluetoothIosPrinting = (): boolean => {
  return isIosApp() && Capacitor.isPluginAvailable('EscPosPrinter');
};

export const supportsUsbWindowsPrinting = (): boolean => {
  return (
    isElectron() &&
    window.electronAPI?.platform === 'win32' &&
    typeof window.electronAPI?.printEscposWindowsRaw === 'function'
  );
};
